/**
 * 폼 초안 보관.
 *
 * 16 §12: "제보 중 앱 종료 → DRAFT 자동 저장".
 * 현장에서 한 손으로 쓰다가 전화가 오면 앱이 내려간다. 그때 쓰던 글이
 * 사라지면 다시 쓰지 않는다 — 그렇게 정보 하나가 영영 안 올라온다.
 *
 * 서버 DRAFT 와는 다르다. 이건 **제출 전** 글자를 지키는 것이고,
 * 서버 DRAFT 는 제출한 뒤 검수 전 상태다.
 */
const PREFIX = "oshimap.draft.";

export function loadDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch { return null; }
}

export function saveDraft(key: string, value: unknown): void {
  try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); }
  catch { /* private mode·용량 초과. 초안을 못 지켜도 폼은 계속 쓸 수 있어야 한다 */ }
}

export function clearDraft(key: string): void {
  try { localStorage.removeItem(PREFIX + key); } catch { /* noop */ }
}

/** 오래된 초안은 도움이 되지 않는다 — 그 사이 현장이 바뀌었다. */
export function isDraftFresh(savedAt: number, maxAgeHours = 24, now = Date.now()): boolean {
  return now - savedAt < maxAgeHours * 3600_000;
}

/**
 * 오래된 초안을 치웁니다.
 *
 * `clearDraft` 는 **제출에 성공했을 때만** 불립니다. 쓰다 만 글, 취소한 제보,
 * 다른 기기에서 마저 쓴 것은 그대로 남습니다. 한 건은 작지만 키가 대상마다
 * (`submit:<placeId>`) 따로 생기므로 계속 늘어납니다. 저장소가 차면 그때부터는
 * **초안뿐 아니라 최근 검색까지** 저장이 통째로 실패합니다 — 원인이 눈에
 * 보이지 않아 찾기 어려운 종류입니다.
 *
 * 읽을 때 이미 `isDraftFresh` 로 거르므로, 지운다고 잃는 것은 없습니다.
 * 시각이 없는 값(최근 검색처럼)은 건드리지 않습니다 — 남의 값을 우리 규칙으로
 * 지우지 않습니다.
 */
export function pruneDrafts(maxAgeHours = 24, now = Date.now()): number {
  let removed = 0;
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(PREFIX)) continue;
      try {
        const value: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
        const savedAt = (value as { savedAt?: unknown } | null)?.savedAt;
        if (typeof savedAt !== "number") continue;
        if (!isDraftFresh(savedAt, maxAgeHours, now)) stale.push(key);
      } catch {
        /* 읽을 수 없는 값은 우리가 쓴 것이 아닐 수 있습니다. 그대로 둡니다. */
      }
    }
    for (const key of stale) { localStorage.removeItem(key); removed += 1; }
  } catch {
    /* private mode 등. 치우지 못해도 앱은 그대로 돌아갑니다. */
  }
  return removed;
}
